import { useEffect, useState } from 'react';
import { usePaymentSelected } from '../../contexts/PaymentSelectedContext';
import {
  TpvHeader,
  TpvSection,
  TpvWrapper,
  TpvButtonSection,
  TpvDescriptionSection,
  TpvHeaderTitle,
  TpvMoneyWrapper,
  TpvPriceText,
  PepperTpvContainer,
} from './TpvStyledComponents';
import { TextBold, TextRegular } from '../../styles/GlobalStyledComponents';
import { PepperTpv } from '@pepper/tpv-react';
import { ButtonsWrapper } from '../../components/Button/ButtonStyledComponent';
import Button from '../../components/Button/Button';
import TokenService from '../../services/Token';
import FinancialService from '../../services/Financial';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTES_PATH } from '../../constants';
import {
  calculateTotalExcusedAmount,
  newPendingPayment,
  pricesHelper,
  savePaymentMethodError,
} from '../../utils/paymentsHelper';
import { useUserResult } from '../../contexts/UserContext';
import { colors } from '../../styles/Colors';
import { PaymentCard } from '../../interfaces/Loan';
import {
  PaymentDataRequest,
  ReusePaymentRequest,
} from '../../interfaces/Payment';
import { PaymentMethod } from '../../interfaces/PaymentMethod';
import { Transition } from '../Transition';

import { InsightService } from '@pepper/insights-react';
import GAnalyticsService from '../../services/GAnalytics/ganalytics.service';
import dayjs from 'dayjs';
import { GTMFlowSteps } from '../../interfaces/GTM';
import { MoneyWrapper } from '../Payment/PaymentStyledComponents';
import IconMoney from '../../assets/icons/IconMoney';
import { ContainerBackgroundMenu } from '../../components/CustomerHeader/CustomerHeaderStyledComponents';
import { DebtResponse, TransactionReasonEnum } from '../../interfaces/Debt';
import { useDebtResult } from '../../contexts/DebtContext';
import { loadFromSessionStorage } from '../../utils/sessionStorageHelper';

const Tpv = () => {
  const { paymentSelected, coPaymentSelected } = usePaymentSelected();
  const { userResult } = useUserResult();
  const { debtResult } = useDebtResult();
  const [initTime, setInitTime] = useState<dayjs.Dayjs>(dayjs());
  const [paymentId, setPaymentId] = useState<string>();
  const [cardList, setCardList] = useState<PaymentCard[]>([]);
  const [loading, setloading] = useState(true);
  const [returnButton, setReturnButton] = useState(false);
  const [thirdParty, setThirdParty] = useState<string>('');
  const [amountExcused, setAmountExcused] = useState<number>(0);
  const { red4, white } = colors;
  const token = TokenService.getToken();
  const userId = userResult?.document_id;
  const search = useLocation().search;
  const paymentMethodSelected = new URLSearchParams(search).get(
    'payment_method'
  );
  const viewMode = sessionStorage.getItem('pepper-view-mode');
  const flowCO = sessionStorage.getItem('flow') === 'co';
  const navigate = useNavigate();

  const environmentType: any = {
    type: process.env.REACT_APP_ENV,
  };

  const getPaymentMethods = async () => {
    if (userId) {
      try {
        const response = await FinancialService.getPaymentCards(userId);
        setCardList(response.payment_methods);
        GAnalyticsService.savePaymentMethodsDataLayer(response);
      } catch (error) {
        GAnalyticsService.sendKoEvent(GTMFlowSteps.TPV);
        savePaymentMethodError(PaymentMethod.CREDIT_CARD);
        navigate(ROUTES_PATH.UNKNOWN_ERROR);
      }
    } else {
      GAnalyticsService.sendKoEvent(GTMFlowSteps.TPV);
      savePaymentMethodError(PaymentMethod.CREDIT_CARD);
      navigate(ROUTES_PATH.UNKNOWN_ERROR);
    }
  };

  const reusePaymentMethodCreated = async (cardCreated: PaymentCard) => {
    if (userId && (paymentSelected || coPaymentSelected)) {
      try {
        const paymentReuseData: ReusePaymentRequest = {
          document_id: userId,
          loan_id: paymentSelected?.loan_id ?? coPaymentSelected?.loan_id ?? '',
          payment_method_id: cardCreated.payment_method_id,
          amount:
            paymentSelected?.amount ?? coPaymentSelected?.certified_debt ?? 0,
          transaction_reason:
            paymentSelected?.transaction_reason ??
            coPaymentSelected?.transaction_reason ??
            TransactionReasonEnum.DEC,
          access_mode: 'FAST_PAY',
        };
        if (flowCO && coPaymentSelected) {
          paymentReuseData.loan_id = coPaymentSelected?.loan_id;
          paymentReuseData.amount = coPaymentSelected.certified_debt ?? 0;
          paymentReuseData.transaction_reason =
            coPaymentSelected.transaction_reason ?? TransactionReasonEnum.DEC;
        }
        if (
          flowCO &&
          ((coPaymentSelected &&
            coPaymentSelected.transaction_reason ===
              TransactionReasonEnum.CPP) ||
            (paymentSelected &&
              paymentSelected.transaction_reason === TransactionReasonEnum.CPP))
        ) {
          if (!loadFromSessionStorage('pepper-payment-plan')) {
            navigate(ROUTES_PATH.UNKNOWN_ERROR);
            return;
          }
        }
        const response = await FinancialService.reusePayment(paymentReuseData);
        GAnalyticsService.saveReusePaymentDataLayer(response);
        GAnalyticsService.sendPaymentInfo(
          paymentReuseData.amount,
          cardCreated.payment_method_id || paymentReuseData.loan_id
        );
        GAnalyticsService.savePaymentCardType(cardCreated.type);
        if (response.status === 'OK') {
          GAnalyticsService.sendContinuePayment(initTime, GTMFlowSteps.TPV, {
            payment_method: PaymentMethod.CREDIT_CARD,
            payment_transaction_reason: paymentReuseData.transaction_reason,
            payment_iin_number: cardCreated.iin_number,
            payment_card_brand: cardCreated.type,
            payment_third_used: thirdParty,
          });
          GAnalyticsService.sendPurchase({
            currency: 'EUR',
            transaction_id: response.loan_id,
            value: response.amount,
            items: [
              {
                itemId: `payment-${response.payment_method_id}`,
                itemName: 'Payment-Landing',
              },
            ],
          });
          setInitTime(dayjs());
          navigate(ROUTES_PATH.SUCCESS);
          // Se guarda el pago realizado en el local storage
          newPendingPayment(response);
        } else {
          GAnalyticsService.sendKoEvent(GTMFlowSteps.TPV);
          savePaymentMethodError(PaymentMethod.CREDIT_CARD);
          navigate(ROUTES_PATH.TYPES_PAYMENTS);
        }
      } catch (error) {
        GAnalyticsService.sendKoEvent(GTMFlowSteps.TPV);
        savePaymentMethodError(PaymentMethod.CREDIT_CARD);
        navigate(ROUTES_PATH.TYPES_PAYMENTS);
      }
    } else {
      GAnalyticsService.sendKoEvent(GTMFlowSteps.TPV);
      savePaymentMethodError(PaymentMethod.CREDIT_CARD);
      navigate(ROUTES_PATH.UNKNOWN_ERROR);
    }
  };

  const btnBackDebtType = () => {
    GAnalyticsService.sendBackDebtType(initTime);
    navigate(
      ROUTES_PATH.TYPES_PAYMENTS + `?payment_method=${paymentMethodSelected}`
    );
  };

  const calculateAmounts = (debtInfo: DebtResponse): number => {
    let excusedAmount = 0;
    if (flowCO) {
      if (coPaymentSelected) {
        const promotion = debtInfo.promotions.find(
          (e) => e.loan_id === coPaymentSelected.loan_id
        );
        excusedAmount = promotion?.amount_excused ?? 0;
      } else {
        excusedAmount =
          debtInfo.promotions.length > 0
            ? calculateTotalExcusedAmount(debtInfo)
            : 0;
      }
      setAmountExcused(excusedAmount);
    } else if (paymentSelected) {
      excusedAmount =
        paymentSelected.amount_excused ??
        debtInfo.promotions.find((e) => e.loan_id === paymentSelected.loan_id)
          ?.amount_excused ??
        0;
      setAmountExcused(excusedAmount);
    }
    return excusedAmount;
  };

  useEffect(() => {
    if (cardList.length > 0) {
      reusePaymentMethodCreated(cardList[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardList]);

  const handleSuccessEnd = () => {
    const amount =
      flowCO && coPaymentSelected && coPaymentSelected.certified_debt
        ? coPaymentSelected.certified_debt
        : paymentSelected && paymentSelected.amount;
    const transactionReason =
      coPaymentSelected?.transaction_reason ??
      paymentSelected?.transaction_reason ??
      TransactionReasonEnum.DEC;
    InsightService.logTrace('TPVSuccess', [
      { paymentMethodSelected: paymentMethodSelected },
      { amount: amount?.toString() },
      {
        amountExcused:
          transactionReason !== TransactionReasonEnum.DEC &&
          TransactionReasonEnum.CPP &&
          TransactionReasonEnum.DE1
            ? amountExcused.toString()
            : '0',
      },
      { loanId: coPaymentSelected?.loan_id ?? paymentSelected?.loan_id ?? '' },
      { transactionReason: transactionReason },
      { route: ROUTES_PATH.TPV },
      { success: true },
    ]);

    if (paymentMethodSelected === PaymentMethod.CREDIT_CARD) {
      getPaymentMethods();
    } else {
      navigate(ROUTES_PATH.SUCCESS);
    }
  };

  const handleError = () => {
    savePaymentMethodError(PaymentMethod.CREDIT_CARD);
    navigate(ROUTES_PATH.TYPES_PAYMENTS);
  };

  const createPayment = async () => {
    if (
      paymentMethodSelected &&
      userId &&
      (paymentSelected || coPaymentSelected)
    ) {
      try {
        setloading(true);
        const urlViewMode = `&viewMode=${viewMode}`;
        const data: PaymentDataRequest = {
          url_ok: `${process.env.REACT_APP_URL}/payment-check/?payment=OK${urlViewMode}`,
          url_ko: `${process.env.REACT_APP_URL}/payment-check/?payment=KO${urlViewMode}`,
          url_cancel: `${process.env.REACT_APP_URL}/payment-check/?payment=CANCEL${urlViewMode}`,
          payment_method: paymentMethodSelected,
          transaction_reason:
            paymentSelected?.transaction_reason ??
            coPaymentSelected?.transaction_reason ??
            TransactionReasonEnum.DEC,
          transaction_type:
            paymentMethodSelected === PaymentMethod.CREDIT_CARD
              ? 'VERIFY'
              : 'PURCHASE',
          document_id: userId,
          // amount: paymentSelected?.amount,
          loan_id: paymentSelected?.loan_id ?? coPaymentSelected?.loan_id ?? '',
        };
        if (flowCO && coPaymentSelected) {
          data.loan_id = coPaymentSelected?.loan_id ?? '';
          data.transaction_reason =
            coPaymentSelected.transaction_reason ?? TransactionReasonEnum.DEC;
        }
        if (
          flowCO &&
          coPaymentSelected &&
          coPaymentSelected.certified_debt &&
          paymentMethodSelected === PaymentMethod.BANK_ACCOUNT
        ) {
          data.amount =
            parseFloat(coPaymentSelected.certified_debt.toFixed(2)) ?? 0;
        } else if (
          paymentSelected &&
          paymentSelected.amount &&
          paymentMethodSelected !== PaymentMethod.CREDIT_CARD
        ) {
          data.amount = parseFloat(paymentSelected.amount.toFixed(2)) ?? 0;
        }
        if (
          flowCO &&
          ((coPaymentSelected &&
            coPaymentSelected.transaction_reason === TransactionReasonEnum.CPP) ||
            (paymentSelected &&
              paymentSelected.transaction_reason === TransactionReasonEnum.CPP))
        ) {
          if (!loadFromSessionStorage('pepper-payment-plan')) {
            navigate(ROUTES_PATH.UNKNOWN_ERROR);
            return;
          }
        }
        const paymentData = await FinancialService.createPayment(data);
        if (paymentData && debtResult) {
          const excusedAmount = calculateAmounts(debtResult);
          setThirdParty(paymentData.third_party);
          GAnalyticsService.savePaymentsDataLayer({
            payment_id: paymentData.payment_id,
            transaction_reason: data.transaction_reason,
            transaction_type: paymentData.transaction_type,
            third_party: paymentData.third_party,
            status: paymentData.status,
            amount_excused: excusedAmount.toString() ?? '0',
          });
        }
        paymentData.self_url = paymentData.self_url.replace(
          'redirect=true',
          'redirect=false'
        );
        setPaymentId(paymentData.payment_id);
      } catch (error) {
        GAnalyticsService.sendKoEvent(GTMFlowSteps.TPV);
        savePaymentMethodError(PaymentMethod.CREDIT_CARD);
        navigate(ROUTES_PATH.TYPES_PAYMENTS);
      } finally {
        setloading(false);
      }
    } else {
      GAnalyticsService.sendKoEvent(GTMFlowSteps.TPV);
      savePaymentMethodError(PaymentMethod.CREDIT_CARD);
      navigate(ROUTES_PATH.UNKNOWN_ERROR);
    }
  };

  useEffect(() => {
    createPayment();
    if (search === '?payment_method=CREDIT_CARD') setReturnButton(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {!loading && (
        <TpvSection id="tpv-page">
          <TpvWrapper id="tpv-wrapper">
            {paymentMethodSelected === 'CREDIT_CARD' && (
              <ContainerBackgroundMenu id="container-background-menu">
                <TpvHeader id="tpv-header">
                  <TpvHeaderTitle>
                    <TextBold fontSize={16} lineHeight={22} fontWeight={800}>
                      Completa tu pago
                    </TextBold>
                    <TextRegular
                      fontSize={14}
                      lineHeight={20}
                      color="gray6"
                      id="tpv-card-title"
                    >
                      Cuando introduzcas la tarjeta se solicitará una
                      autorización para realizar el pago.
                    </TextRegular>
                  </TpvHeaderTitle>
                  <TpvMoneyWrapper id="debt-detail-debt-wrapper">
                    <MoneyWrapper id="debt-detail-money-wrapper">
                      <IconMoney />
                    </MoneyWrapper>
                    <TpvDescriptionSection id="debt-detail-description-section">
                      <TextRegular fontSize={14} color="gray6" lineHeight={20}>
                        Importe a pagar
                      </TextRegular>
                      <TpvPriceText
                        fontSize={18}
                        lineHeight={24}
                        id="tpv-price-text"
                      >
                        {paymentSelected &&
                          !coPaymentSelected &&
                          pricesHelper(paymentSelected.amount)}
                        {flowCO &&
                          coPaymentSelected &&
                          pricesHelper(coPaymentSelected.certified_debt ?? 0)}
                        <TextRegular fontSize={12} lineHeight={16}>
                          &nbsp;€
                        </TextRegular>
                      </TpvPriceText>
                    </TpvDescriptionSection>
                  </TpvMoneyWrapper>
                </TpvHeader>
              </ContainerBackgroundMenu>
            )}
            {/* <div> */}
            {paymentId && token && (
              <PepperTpvContainer id="pepper-tpv-container">
                <PepperTpv
                  token={token}
                  paymentId={paymentId}
                  successEnd={handleSuccessEnd}
                  error={handleError}
                  currentEnvironment={environmentType}
                  subtitle={''}
                  subtitlePosition="top"
                  generalPadding="15px 20px 40px 20px"
                  saveRedirectUrls={(e) => {}}
                  isIframe
                  isRedirection={paymentMethodSelected === 'BANK_ACCOUNT'}
                  showInWidget={true}
                  buttonBackgroundColor={red4}
                  buttonTextColor={white}
                  appInsightKey={process.env.REACT_APP_INSTRUMENTATION_KEY}
                  origin={'LP'} //Permite saltar la pantalla de error clásica e ir a los métodos de pago directamente
                />
              </PepperTpvContainer>
            )}
            {returnButton && paymentId && token && (
              <TpvButtonSection id="buttons-section">
                <ButtonsWrapper id="wrapped-buttons">
                  <Button
                    id="payment-back-btn"
                    color={red4}
                    border={'2px'}
                    borderColor={red4}
                    backgroundColor={white}
                    click={() => btnBackDebtType()}
                  >
                    <TextBold fontSize={18} color="red4" lineHeight={20}>
                      Cambia el método de pago
                    </TextBold>
                  </Button>
                </ButtonsWrapper>
              </TpvButtonSection>
            )}
          </TpvWrapper>
        </TpvSection>
      )}
      {loading && <Transition />}
    </>
  );
};

export default Tpv;
